import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["navItems", "defaultExpanded", "defaultSelected", "onBeforeSelection", "isMobile", "useArrowKeys", "role", "aria-current", "className", "id"];
/*
 * © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useId, useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { StyledNavItem } from './verticalNavItem';
import { VerticalNavDot } from './verticalNavDot';
import { List, Collapse } from '@mui/material';
import { LINE_TOP_OFFSET_CHILD, LINE_TOP_OFFSET_PRIMARY, TREE_NODE_HEIGHT } from './verticalNavStyles';
var POSTFIX_ICON_CLASS = 'EmbersNav-item-postfixIcon';
export var EmbersVerticalNav = function EmbersVerticalNav(props) {
  var navItems = props.navItems,
    defaultExpanded = props.defaultExpanded,
    defaultSelected = props.defaultSelected,
    onBeforeSelection = props.onBeforeSelection,
    isMobile = props.isMobile,
    _props$useArrowKeys = props.useArrowKeys,
    useArrowKeys = _props$useArrowKeys === void 0 ? true : _props$useArrowKeys,
    role = props.role,
    ariaCurrent = props['aria-current'],
    className = props.className,
    id = props.id,
    otherProps = _objectWithoutProperties(props, _excluded);
  var navigationId = useId() || id;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    expandedList = _useState2[0],
    setExpandedList = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedItem = _useState4[0],
    setSelected = _useState4[1];
  var rootNodeIds = useMemo(function () {
    return navItems.map(function (item) {
      return item.nodeId;
    });
  }, [navItems]);
  useEffect(function () {
    // the first primary navigation item will be selected and expanded by default
    // if not otherwise specified by the selected and expanded props
    var firstId = rootNodeIds[0];
    defaultSelected ? setSelected(defaultSelected) : setSelected(firstId);
  }, [defaultSelected]);
  useEffect(function () {
    defaultExpanded && setExpandedList(defaultExpanded);
  }, [defaultExpanded]);
  var renderTree = function renderTree(items, level, parentNode) {
    return items.map(function (item) {
      var isPrimary = function isPrimary() {
        return level === 0;
      };
      var getHeight = function getHeight(subNodes) {
        var height = 0;
        if (subNodes === undefined) {
          return 0;
        }
        subNodes.forEach(function (subNode) {
          height += expandedList.includes(subNode.nodeId || '') ? getHeight(subNode.childNodes) + TREE_NODE_HEIGHT : TREE_NODE_HEIGHT;
        });
        return height;
      };
      var itemExpanded = expandedList.includes(item.nodeId);
      var treeChildren;
      var lineOffset = isPrimary() ? LINE_TOP_OFFSET_PRIMARY : LINE_TOP_OFFSET_CHILD;
      var lineHeight = expandedList ? getHeight(item.childNodes) - lineOffset : 0;
      if (item.childNodes) {
        treeChildren = renderTree(item.childNodes, level + 1, isPrimary() ? item.nodeId : parentNode);
        if (item.childNodes.length > 0 && itemExpanded && expandedList.includes(item.childNodes[item.childNodes.length - 1].nodeId)) {
          treeChildren.push( /*#__PURE__*/React.createElement(VerticalNavDot, {
            level: level,
            lineHeight: lineHeight,
            lineOffset: lineOffset,
            key: 'verticalNavDot_' + item.nodeId,
            isBottomDot: true
          }));
        }
      }
      var toggleItemExpand = function toggleItemExpand() {
        if (item.childNodes) {
          var newExpanded = [];
          if (expandedList.includes(item.nodeId)) {
            newExpanded = expandedList.filter(function (id) {
              return id !== item.nodeId;
            });
          } else {
            if (rootNodeIds.includes(item.nodeId)) {
              // When one primary navigation section is clicked to expand it, the other primary sections will close.
              newExpanded = expandedList.filter(function (id) {
                return !rootNodeIds.includes(id);
              });
              newExpanded.unshift(item.nodeId);
            } else {
              newExpanded = [item.nodeId].concat(_toConsumableArray(expandedList));
            }
          }
          setExpandedList(newExpanded);
        }
      };
      var updateSelectedItem = function updateSelectedItem() {
        var stay = onBeforeSelection === null || onBeforeSelection === void 0 ? void 0 : onBeforeSelection(item.nodeId, item.path, item.label, item.childNodes);
        if (!stay) {
          setSelected(item.nodeId);
        }
      };
      var handleClick = function handleClick(e) {
        if (item.disabled) {
          return;
        }
        var currentTarget = e.target;
        var classList = _toConsumableArray(currentTarget.classList);
        if (e.code === 'Enter') {
          updateSelectedItem();
        } else if (e.code === 'Space') {
          toggleItemExpand();
        } else if (!e.code) {
          if (classList.includes('icon-expand-less') || classList.includes('icon-expand-more') || classList.includes(POSTFIX_ICON_CLASS)) {
            toggleItemExpand();
          } else {
            updateSelectedItem();
          }
        }
      };

      // manually set focus on provided node
      var setFocus = function setFocus(node) {
        var _node$firstChild;
        // @ts-ignore
        node === null || node === void 0 || (_node$firstChild = node.firstChild) === null || _node$firstChild === void 0 || _node$firstChild.focus();
      };

      // need to account for list item grouping for submenus
      var getListItem = function getListItem(node) {
        return (node === null || node === void 0 ? void 0 : node.nodeName) === 'LI' ? node : node === null || node === void 0 ? void 0 : node.firstChild;
      };
      var findParentElement = function findParentElement(target, className) {
        var parent = target.parentNode;
        while (parent) {
          if (parent.nodeName === 'BODY') {
            break;
          }
          if (parent.classList.contains(className)) {
            return parent;
          }
          parent = parent.parentNode;
        }
        return null;
      };
      var getNextTabIndexElement = function getNextTabIndexElement(currentElement, containerId, dir) {
        try {
          var nextElement = null;
          if (currentElement) {
            var allTabbedElements = document.querySelectorAll(containerId ? "[id=\"".concat(containerId, "\"] [tabindex=\"0\"]") : '[tabindex="0"]');
            var tabbableElements = allTabbedElements ? Array.from(allTabbedElements) : [];
            var currentIndex = tabbableElements.indexOf(currentElement);
            if (currentIndex !== -1) {
              var nextIndex = dir === 'previous' ? currentIndex - 1 : currentIndex + 1;
              nextElement = tabbableElements[nextIndex];
            }
          }
          return nextElement;
        } catch (e) {
          return null;
        }
      };
      var controlFocusByKey = function controlFocusByKey(keyboardEvent) {
        var _focusParent, _focusParent2, _focusParent5, _focusParent6;
        var activeElement = document.activeElement;
        var focusParent = activeElement === null || activeElement === void 0 ? void 0 : activeElement.parentElement;
        var isPostfixIconFocused = (_focusParent = focusParent) === null || _focusParent === void 0 ? void 0 : _focusParent.classList.contains(POSTFIX_ICON_CLASS);
        var submenuId;
        var isSubMenuFocused;
        if ((_focusParent2 = focusParent) !== null && _focusParent2 !== void 0 && (_focusParent2 = _focusParent2.parentElement) !== null && _focusParent2 !== void 0 && (_focusParent2 = _focusParent2.getAttribute('id')) !== null && _focusParent2 !== void 0 && _focusParent2.includes('-listItem')) {
          var _focusParent3;
          focusParent = (_focusParent3 = focusParent) === null || _focusParent3 === void 0 ? void 0 : _focusParent3.parentElement;
        }
        if (isPostfixIconFocused) {
          var _submenuId;
          var submenuNav = findParentElement(focusParent, 'MuiList-root');
          submenuId = submenuNav === null || submenuNav === void 0 ? void 0 : submenuNav.getAttribute('id');
          isSubMenuFocused = (_submenuId = submenuId) === null || _submenuId === void 0 ? void 0 : _submenuId.includes('-submenu');
        } else {
          var _focusParent4, _submenuId2;
          submenuId = (_focusParent4 = focusParent) === null || _focusParent4 === void 0 || (_focusParent4 = _focusParent4.parentElement) === null || _focusParent4 === void 0 ? void 0 : _focusParent4.getAttribute('id');
          isSubMenuFocused = (_submenuId2 = submenuId) === null || _submenuId2 === void 0 ? void 0 : _submenuId2.includes('-submenu');
        }
        switch (keyboardEvent.key) {
          case 'ArrowUp':
          case 'ArrowLeft':
            {
              keyboardEvent.preventDefault();
              var previousTabIndexElement = getNextTabIndexElement(activeElement, isSubMenuFocused ? submenuId : navigationId, 'previous');
              if (previousTabIndexElement) {
                setFocus(previousTabIndexElement.parentElement);
              }
              break;
            }
          case 'ArrowDown':
          case 'ArrowRight':
            {
              keyboardEvent.preventDefault();
              var nextTabIndexElement = getNextTabIndexElement(activeElement, isSubMenuFocused ? submenuId : navigationId, 'next');
              if (nextTabIndexElement) {
                nextTabIndexElement.focus();
              }
              break;
            }
          case 'Home':
            keyboardEvent.preventDefault();
            setFocus(getListItem((_focusParent5 = focusParent) === null || _focusParent5 === void 0 || (_focusParent5 = _focusParent5.parentElement) === null || _focusParent5 === void 0 ? void 0 : _focusParent5.firstChild));
            break;
          case 'End':
            keyboardEvent.preventDefault();
            setFocus(getListItem((_focusParent6 = focusParent) === null || _focusParent6 === void 0 || (_focusParent6 = _focusParent6.parentElement) === null || _focusParent6 === void 0 ? void 0 : _focusParent6.lastChild));
            break;
          case ' ':
          case 'Enter':
            {
              keyboardEvent.preventDefault();
              if (isPostfixIconFocused) {
                handleClick({
                  target: activeElement
                });
              } else {
                handleClick(keyboardEvent);
              }
              break;
            }
        }
      };
      var handleKeyDown = function handleKeyDown(event) {
        // collapse to root node on escape
        if (event.key === 'Escape') {
          var _document$activeEleme, _document$getElementB;
          var closeNode = parentNode || ((_document$activeEleme = document.activeElement) === null || _document$activeEleme === void 0 ? void 0 : _document$activeEleme.id);
          var newExpanded = expandedList.filter(function (id) {
            return id !== closeNode;
          });
          setExpandedList(newExpanded);
          closeNode && ((_document$getElementB = document.getElementById(closeNode)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.focus());
        }

        // move focus into the open menu if the current menu is open
        else if (useArrowKeys && itemExpanded && (event.key === 'ArrowDown' || event.key === 'ArrowRight')) {
          var _document$getElementB2;
          event.preventDefault();
          var listItem = (_document$getElementB2 = document.getElementById("".concat(item.nodeId, "-submenu"))) === null || _document$getElementB2 === void 0 ? void 0 : _document$getElementB2.firstChild;
          setFocus(getListItem(listItem));
        }

        // handle arrow key and home/end navigation
        else if (useArrowKeys) {
          controlFocusByKey(event);
        }
      };
      var navItemProps = {
        id: item.nodeId,
        key: item.nodeId,
        level: level,
        isPrimary: isPrimary(),
        lineHeight: lineHeight,
        expandedList: expandedList,
        expanded: itemExpanded,
        isMobile: isMobile,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        selected: selectedItem === item.nodeId,
        'aria-current': ariaCurrent
      };
      return item.childNodes ? /*#__PURE__*/React.createElement("div", {
        key: "".concat(item.nodeId, "-listItem"),
        id: "".concat(item.nodeId, "-listItem")
      }, /*#__PURE__*/React.createElement(StyledNavItem, _extends({}, item, navItemProps)), /*#__PURE__*/React.createElement(Collapse, {
        className: "EmbersNav-list-group",
        in: itemExpanded,
        timeout: "auto",
        unmountOnExit: true
      }, /*#__PURE__*/React.createElement(List, {
        id: "".concat(item.nodeId, "-submenu"),
        key: "".concat(level, ":").concat(item.nodeId),
        component: "nav",
        sx: {
          height: '100%',
          width: 'auto'
        },
        disablePadding: true
      }, treeChildren))) : /*#__PURE__*/React.createElement(StyledNavItem, _extends({}, item, navItemProps), treeChildren);
    });
  };
  return /*#__PURE__*/React.createElement(List, _extends({
    role: role || 'navigation',
    id: navigationId,
    className: classnames('Embers-verticalNav-root', className)
  }, otherProps), renderTree(navItems, 0));
};